import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { Search, Clock, Users, ChefHat, Star, Filter, Plus, UtensilsCrossed, Sparkles, X } from "lucide-react";

const SORT_LABELS = { featured: "Featured", popular: "Popular", rating: "Highest rated", new: "Newest", quick: "Quickest" };

function StarRow({ avg = 0, count = 0 }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs" title={`${avg.toFixed(1)} (${count})`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={11} strokeWidth={2}
          style={{ color: i <= Math.round(avg) ? "#c47f3a" : "#c8c1af", fill: i <= Math.round(avg) ? "#c47f3a" : "transparent" }} />
      ))}
      <span style={{ color: "var(--wofy-text-soft)" }}>{count > 0 ? `${avg.toFixed(1)} · ${count}` : "New"}</span>
    </span>
  );
}

function Card({ r }) {
  const totalMin = r.total_time_min || (r.prep_time_min + r.cook_time_min);
  const blurb = (() => {
    const src = r.ai_summary || r.short_description || "";
    if (!src) return "";
    if (src.length <= 150) return src;
    const cut = src.slice(0, 150);
    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > 100 ? cut.slice(0, lastSpace) : cut).trimEnd() + "…";
  })();
  const richTags = [
    r.ripeness && `${r.ripeness}`,
    r.variety,
    r.authenticity_type,
    ...(Array.isArray(r.festivals) ? r.festivals : []),
  ].filter(Boolean).slice(0, 3);
  return (
    <Link
      to={`/recipes/${r.slug}`}
      data-testid={`recipe-card-${r.id}`}
      className="flex flex-col rounded-lg overflow-hidden border transition hover:shadow-lg hover:-translate-y-0.5"
      style={{ borderColor: "var(--wofy-line)", background: "var(--wofy-cream, #FDFBF7)" }}
    >
      <div className="w-full aspect-[4/3] bg-[var(--wofy-card-bg,#f2ede4)] flex items-center justify-center overflow-hidden">
        {r.cover_photo ? (
          <img src={r.cover_photo} alt={r.title} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <UtensilsCrossed size={48} style={{ color: "var(--wofy-primary)" }} />
        )}
      </div>
      <div className="p-4 flex flex-col flex-1 gap-2">
        <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider" style={{ color: "var(--wofy-primary)" }}>
          <span>{r.cuisine}</span>
          {r.country && <><span>·</span><span>{r.country}</span></>}
          {r.region && <><span>·</span><span>{r.region}</span></>}
          {r.featured && <span className="ml-auto px-2 py-0.5 rounded-full font-bold" style={{ background: "#c47f3a", color: "white" }}>FEATURED</span>}
        </div>
        <h3 className="font-display text-lg leading-snug">{r.title}</h3>
        <p className="text-xs leading-relaxed line-clamp-3" style={{ color: "var(--wofy-text-soft)" }}>
          {blurb}
        </p>
        <div className="mt-auto pt-2 flex items-center gap-3 text-xs flex-wrap" style={{ color: "var(--wofy-text-soft)" }}>
          <span className="inline-flex items-center gap-1"><Clock size={11} /> {totalMin || 0} min</span>
          <span className="inline-flex items-center gap-1"><Users size={11} /> {r.servings || 4}</span>
          <span className="inline-flex items-center gap-1"><ChefHat size={11} /> {r.difficulty}</span>
          <StarRow avg={r.rating_avg || 0} count={r.rating_count || 0} />
        </div>
        {(r.diet_flags && r.diet_flags.length > 0 || richTags.length > 0 || r.waste_less_tip) && (
          <div className="flex gap-1 flex-wrap">
            {r.waste_less_tip && (
              <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: "#EDF5E4", color: "#3E5A2A" }} title={r.waste_less_tip}>♻ Waste-less</span>
            )}
            {(r.diet_flags || []).slice(0, 2).map((f) => (
              <span key={`d-${f}`} className="text-[10px] px-2 py-0.5 rounded" style={{ background: "rgba(92,107,82,0.15)", color: "#5C6B52" }}>{f.replace("_", " ")}</span>
            ))}
            {richTags.map((t) => (
              <span key={`t-${t}`} className="text-[10px] px-2 py-0.5 rounded" style={{ background: "#FBF2DE", color: "#8A5A24" }}>{t}</span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}

function PantryMatchWidget() {
  const [pantry, setPantry] = useState([]);   // list of {slug, name}
  const [input, setInput] = useState("");
  const [suggest, setSuggest] = useState([]);
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);

  // Autocomplete against food_items list
  useEffect(() => {
    if (input.trim().length < 2) { setSuggest([]); return; }
    const t = setTimeout(() => {
      http.get("/food-items", { params: { q: input.trim(), limit: 8, name_only: true } })
        .then((r) => setSuggest(r.data.items || r.data || []))
        .catch(() => setSuggest([]));
    }, 180);
    return () => clearTimeout(t);
  }, [input]);

  const addItem = (slug, name) => {
    if (!slug) return;
    if (pantry.some((p) => p.slug === slug)) return;
    setPantry((p) => [...p, { slug, name: name || slug }]);
    setInput("");
    setSuggest([]);
  };

  const removeItem = (slug) => setPantry((p) => p.filter((x) => x.slug !== slug));

  const findRecipes = async () => {
    if (pantry.length === 0) return;
    setLoading(true);
    try {
      const r = await http.post("/recipes/pantry-match", {
        ingredients: pantry.map((p) => p.slug), min_match_pct: 40, limit: 20,
      });
      setMatches(r.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mb-8 rounded-2xl border p-5 md:p-6" data-testid="pantry-match-widget"
         style={{ borderColor: "#DCEAD1", background: "#F5FAF0" }}>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={16} style={{ color: "#5C6B52" }} />
        <h2 className="font-display text-xl">What can I cook with what I have?</h2>
      </div>
      <p className="text-xs mb-4" style={{ color: "var(--wofy-text-soft)" }}>
        Type ingredients you have on hand — we'll rank recipes by how well they match your pantry.
      </p>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="pantry-chips">
        {pantry.map((p) => (
          <span key={p.slug}
                className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs bg-white border"
                style={{ borderColor: "#DCEAD1" }}
                data-testid={`pantry-chip-${p.slug}`}>
            {p.name}
            <button onClick={() => removeItem(p.slug)} aria-label={`Remove ${p.name}`} className="opacity-60 hover:opacity-100">
              <X size={12} />
            </button>
          </span>
        ))}
      </div>

      <div className="relative">
        <div className="flex items-center gap-2 border rounded-full px-4 py-2.5 bg-white" style={{ borderColor: "#DCEAD1" }}>
          <Search size={14} style={{ color: "var(--wofy-text-soft)" }} />
          <input
            data-testid="pantry-match-input"
            type="text"
            placeholder="Add an ingredient — e.g. banana, egg, rice, tomato (press Enter to add)"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (suggest[0]) {
                  addItem(suggest[0].slug, suggest[0].common_name);
                } else if (input.trim().length >= 2) {
                  const raw = input.trim();
                  addItem(raw.toLowerCase(), raw);
                }
              }
            }}
            className="flex-1 bg-transparent outline-none text-sm"
          />
          <button
            data-testid="pantry-match-find-btn"
            onClick={findRecipes}
            disabled={pantry.length === 0 || loading}
            className="text-xs font-semibold px-3 py-1.5 rounded-full disabled:opacity-50"
            style={{ background: "#5C6B52", color: "white" }}>
            {loading ? "Finding…" : "Find recipes"}
          </button>
        </div>
        {suggest.length > 0 && (
          <div className="absolute z-10 top-full left-0 right-0 mt-1 border rounded-lg bg-white overflow-hidden shadow-md"
               style={{ borderColor: "#DCEAD1" }}
               data-testid="pantry-match-suggestions">
            {suggest.map((s) => (
              <button key={s.slug}
                      onClick={() => addItem(s.slug, s.common_name)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5FAF0] border-b last:border-b-0"
                      style={{ borderColor: "#F0F0F0" }}>
                <span>{s.common_name}</span>
                {s.category && <span className="ml-2 text-xs opacity-60">· {s.category}</span>}
              </button>
            ))}
            {input.trim().length >= 2 && (
              <button onClick={() => { const raw = input.trim(); addItem(raw.toLowerCase(), raw); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#F5FAF0] italic opacity-70">
                Add "{input.trim()}" as free-text ingredient
              </button>
            )}
          </div>
        )}
      </div>

      {matches && matches.items && matches.items.length > 0 && (
        <div className="mt-5" data-testid="pantry-match-results">
          <div className="text-xs mb-2 font-mono uppercase tracking-widest opacity-70">
            {matches.total} matching recipes
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {matches.items.map((r) => (
              <Link key={r.id} to={`/recipes/${r.slug}`}
                    className="block rounded-xl border p-3 bg-white hover:-translate-y-0.5 hover:shadow transition-all"
                    style={{ borderColor: "#DCEAD1" }}
                    data-testid={`pantry-match-recipe-${r.slug}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">
                    {r.cuisine || r.country || "Recipe"}
                  </span>
                  <span className="text-xs font-semibold" style={{ color: r.percentage_match >= 80 ? "#3E5A2A" : "#8A5A24" }}>
                    {r.percentage_match}% match
                  </span>
                </div>
                <div className="font-display text-sm mb-1 line-clamp-2">{r.title}</div>
                <div className="text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
                  You have <strong>{(r.ingredients_have || []).length}</strong> of {(r.ingredients_have || []).length + (r.ingredients_missing || []).length}
                  {(r.ingredients_missing || []).length > 0 && (
                    <span> · missing: {r.ingredients_missing.slice(0, 3).map((i) => i.name).join(", ")}{r.ingredients_missing.length > 3 ? "…" : ""}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {matches && matches.items && matches.items.length === 0 && (
        <div className="mt-4 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          No recipes match those ingredients with ≥40%. Try adding a few more items or removing one.
        </div>
      )}
    </div>
  );
}

export default function Recipes() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const filters = useMemo(() => ({
    q: searchParams.get("q") || "",
    category: searchParams.get("category") || "all",
    cuisine: searchParams.get("cuisine") || "all",
    country: searchParams.get("country") || "all",
    diet: searchParams.get("diet") || "",
    difficulty: searchParams.get("difficulty") || "all",
    max_time: searchParams.get("max_time") || "",
    sort: searchParams.get("sort") || "featured",
    // ── New rich filters
    ripeness: searchParams.get("ripeness") || "all",
    festival: searchParams.get("festival") || "all",
    meal_category: searchParams.get("meal_category") || "all",
    cooking_method: searchParams.get("cooking_method") || "all",
    authenticity_type: searchParams.get("authenticity_type") || "all",
    surplus_use: searchParams.get("surplus_use") || "all",
    waste_less: searchParams.get("waste_less") === "1",
    ingredient: searchParams.get("ingredient") || "",
    exclude_allergen: searchParams.get("exclude_allergen") || "",
  }), [searchParams]);

  useEffect(() => { http.get("/recipes/config").then((r) => setConfig(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 60, sort: filters.sort };
    if (filters.q) params.q = filters.q;
    if (filters.category !== "all") params.category = filters.category;
    if (filters.cuisine !== "all") params.cuisine = filters.cuisine;
    if (filters.country !== "all") params.country = filters.country;
    if (filters.diet) params.diet = filters.diet;
    if (filters.difficulty !== "all") params.difficulty = filters.difficulty;
    if (filters.max_time) params.max_time = Number(filters.max_time);
    if (filters.ripeness !== "all") params.ripeness = filters.ripeness;
    if (filters.festival !== "all") params.festival = filters.festival;
    if (filters.meal_category !== "all") params.meal_category = filters.meal_category;
    if (filters.cooking_method !== "all") params.cooking_method = filters.cooking_method;
    if (filters.authenticity_type !== "all") params.authenticity_type = filters.authenticity_type;
    if (filters.surplus_use !== "all") params.surplus_use = filters.surplus_use;
    if (filters.waste_less) params.waste_less = "true";
    if (filters.ingredient) params.ingredient = filters.ingredient;
    if (filters.exclude_allergen) params.exclude_allergen = filters.exclude_allergen;
    http.get("/recipes", { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [filters]);

  const setFilter = (key, val) => {
    const next = new URLSearchParams(searchParams);
    if (!val || val === "all" || val === false || val === "false") next.delete(key);
    else next.set(key, val === true ? "1" : val);
    setSearchParams(next, { replace: true });
  };

  const activeRichFilterCount = [
    filters.country, filters.ripeness, filters.festival, filters.meal_category,
    filters.cooking_method, filters.authenticity_type, filters.surplus_use,
  ].filter((v) => v && v !== "all").length + (filters.waste_less ? 1 : 0) +
    (filters.ingredient ? 1 : 0) + (filters.exclude_allergen ? 1 : 0);

  const clearAll = () => setSearchParams(new URLSearchParams(), { replace: true });

  return (
    <div data-testid="recipes-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      {/* Hero */}
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
            <UtensilsCrossed size={13} className="inline mr-1.5 -mt-0.5" />
            RECIPES OF THE DAY
          </div>
          <h1 className="font-display text-4xl md:text-5xl leading-tight">The kitchen of the world.</h1>
          <p className="text-sm mt-2 max-w-[52ch]" style={{ color: "var(--wofy-text-soft)" }}>
            A permanent, growing collection of family, cultural, and modern recipes shared by our community.
            Every recipe preserves the people, traditions, and stories behind it.
          </p>
        </div>
        <Link
          data-testid="recipe-submit-btn"
          to="/recipes/submit"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm"
          style={{ background: "var(--wofy-primary)", color: "white" }}
        >
          <Plus size={14} /> Share a recipe
        </Link>
      </div>

      {/* Pantry Match — "What can I cook with what I have?" */}
      <PantryMatchWidget />

      {/* Search bar */}
      <div className="mb-6 flex items-center gap-2 border rounded-full px-4 py-3" style={{ borderColor: "var(--wofy-line)", background: "var(--wofy-cream,#FDFBF7)" }}>
        <Search size={16} style={{ color: "var(--wofy-text-soft)" }} />
        <input
          data-testid="recipe-search-input"
          type="text"
          placeholder="Search recipes, ingredients, cuisines, or countries…"
          defaultValue={filters.q}
          onKeyDown={(e) => { if (e.key === "Enter") setFilter("q", e.currentTarget.value); }}
          onBlur={(e) => setFilter("q", e.currentTarget.value)}
          className="flex-1 bg-transparent outline-none text-sm"
        />
      </div>

      {/* Filter row */}
      {config && (
        <div className="mb-6 flex flex-wrap gap-3 items-center text-sm">
          <Filter size={14} style={{ color: "var(--wofy-text-soft)" }} />
          <select data-testid="recipe-filter-cat" className="border rounded px-3 py-1.5 text-sm" value={filters.category} onChange={(e) => setFilter("category", e.target.value)}>
            <option value="all">All categories</option>
            {config.categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="recipe-filter-cuisine" className="border rounded px-3 py-1.5 text-sm" value={filters.cuisine} onChange={(e) => setFilter("cuisine", e.target.value)}>
            <option value="all">All cuisines</option>
            {config.cuisines.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          {config.countries && config.countries.length > 0 && (
            <select data-testid="recipe-filter-country" className="border rounded px-3 py-1.5 text-sm" value={filters.country} onChange={(e) => setFilter("country", e.target.value)}>
              <option value="all">All countries</option>
              {config.countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <select data-testid="recipe-filter-diff" className="border rounded px-3 py-1.5 text-sm" value={filters.difficulty} onChange={(e) => setFilter("difficulty", e.target.value)}>
            <option value="all">All levels</option>
            {config.difficulties.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="recipe-filter-diet" className="border rounded px-3 py-1.5 text-sm" value={filters.diet} onChange={(e) => setFilter("diet", e.target.value)}>
            <option value="">Any diet</option>
            {config.diet_flags.map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select>
          <select data-testid="recipe-filter-time" className="border rounded px-3 py-1.5 text-sm" value={filters.max_time} onChange={(e) => setFilter("max_time", e.target.value)}>
            <option value="">Any time</option>
            <option value="15">≤ 15 min</option>
            <option value="30">≤ 30 min</option>
            <option value="60">≤ 1 h</option>
            <option value="120">≤ 2 h</option>
          </select>
          <select data-testid="recipe-filter-sort" className="border rounded px-3 py-1.5 text-sm ml-auto" value={filters.sort} onChange={(e) => setFilter("sort", e.target.value)}>
            {Object.entries(SORT_LABELS).map(([k, v]) => <option key={k} value={k}>Sort: {v}</option>)}
          </select>
        </div>
      )}

      {/* Rich-tag filter row (v1 pipeline data) */}
      {config && (config.ripeness_stages?.length > 0 || config.festivals?.length > 0 ||
                  config.meal_categories?.length > 0 || config.cooking_methods?.length > 0) && (
        <div className="mb-6 flex flex-wrap gap-2 items-center text-xs">
          {config.ripeness_stages?.length > 0 && (
            <select data-testid="recipe-filter-ripeness" className="border rounded px-3 py-1.5" value={filters.ripeness} onChange={(e) => setFilter("ripeness", e.target.value)}>
              <option value="all">Any ripeness</option>
              {config.ripeness_stages.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {config.festivals?.length > 0 && (
            <select data-testid="recipe-filter-festival" className="border rounded px-3 py-1.5" value={filters.festival} onChange={(e) => setFilter("festival", e.target.value)}>
              <option value="all">Any festival</option>
              {config.festivals.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {config.meal_categories?.length > 0 && (
            <select data-testid="recipe-filter-meal" className="border rounded px-3 py-1.5" value={filters.meal_category} onChange={(e) => setFilter("meal_category", e.target.value)}>
              <option value="all">Any meal</option>
              {config.meal_categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {config.cooking_methods?.length > 0 && (
            <select data-testid="recipe-filter-method" className="border rounded px-3 py-1.5" value={filters.cooking_method} onChange={(e) => setFilter("cooking_method", e.target.value)}>
              <option value="all">Any method</option>
              {config.cooking_methods.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {config.authenticity_types?.length > 0 && (
            <select data-testid="recipe-filter-authenticity" className="border rounded px-3 py-1.5" value={filters.authenticity_type} onChange={(e) => setFilter("authenticity_type", e.target.value)}>
              <option value="all">Any style</option>
              {config.authenticity_types.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {config.surplus_options?.length > 0 && (
            <select data-testid="recipe-filter-surplus" className="border rounded px-3 py-1.5" value={filters.surplus_use} onChange={(e) => setFilter("surplus_use", e.target.value)}>
              <option value="all">Any surplus fit</option>
              {config.surplus_options.map((c) => <option key={c} value={c}>Surplus · {c}</option>)}
            </select>
          )}
          {config.allergens?.length > 0 && (
            <select data-testid="recipe-filter-allergen" className="border rounded px-3 py-1.5" value={filters.exclude_allergen} onChange={(e) => setFilter("exclude_allergen", e.target.value)}>
              <option value="">No allergen filter</option>
              {config.allergens.map((c) => <option key={c} value={c}>Exclude · {c}</option>)}
            </select>
          )}
          <label className="inline-flex items-center gap-1.5 border rounded-full px-3 py-1.5 cursor-pointer bg-white" data-testid="recipe-filter-wasteless">
            <input type="checkbox" checked={filters.waste_less} onChange={(e) => setFilter("waste_less", e.target.checked)} className="accent-[#5C6B52]" />
            <span>♻ Waste-less only</span>
          </label>
          {activeRichFilterCount > 0 && (
            <button onClick={clearAll} className="ml-auto text-xs underline opacity-70 hover:opacity-100" data-testid="recipe-filter-clear">
              Clear filters ({activeRichFilterCount})
            </button>
          )}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="py-16 text-center" style={{ color: "var(--wofy-text-soft)" }}>Loading recipes…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center border rounded-lg" style={{ borderColor: "var(--wofy-line)", color: "var(--wofy-text-soft)" }}>
          No recipes match these filters yet. Adjust the search, or <Link className="underline" to="/recipes/submit" style={{ color: "var(--wofy-primary)" }}>share the first one</Link>.
        </div>
      ) : (
        <>
          <div className="text-xs mb-3" style={{ color: "var(--wofy-text-soft)" }}>{total} recipes</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {items.map((r) => (<Card key={r.id} r={r} />))}
          </div>
        </>
      )}
    </div>
  );
}
